import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/lib/auth";
import {
    LayoutDashboard,
    Inbox,
    FolderOpen,
    ImageIcon,
    Sparkles,
    SplitSquareHorizontal,
    Compass,
    FileText,
    Home as HomeIcon,
    Anchor,
    QrCode,
    BarChart3,
    Users,
    ShieldCheck,
    LogOut,
    ExternalLink,
    MousePointer,
    Megaphone,
    Newspaper,
    Scale,
} from "lucide-react";

const NAV = [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
    { to: "/admin/tagesbericht", label: "Tagesbericht", icon: FileText, testId: "admin-nav-tagesbericht" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "admin-nav-analytics" },
    { to: "/admin/anfragen", label: "Anfragen", icon: Inbox, testId: "admin-nav-anfragen" },
    { to: "/admin/vertreter", label: "Vertreter", icon: Users, testId: "admin-nav-vertreter" },
    { to: "/admin/projekte", label: "Projekte", icon: FolderOpen, testId: "admin-nav-projekte" },
    { to: "/admin/medien", label: "Medien", icon: ImageIcon, testId: "admin-nav-medien" },
    { to: "/admin/vorher-nachher", label: "Vorher-Nachher-Generator", icon: SplitSquareHorizontal, testId: "admin-nav-vorher-nachher" },
    { to: "/admin/vorher-nachher-360", label: "360°-Vorher-Nachher-Generator", icon: Compass, testId: "admin-nav-vorher-nachher-360" },
    { to: "/admin/skybox", label: "Skybox 360°", icon: Sparkles, testId: "admin-nav-skybox" },
    { to: "/admin/startseite", label: "Startseite", icon: HomeIcon, testId: "admin-nav-startseite" },
    { to: "/admin/vertrauen", label: "Vertrauens-Box", icon: ShieldCheck, testId: "admin-nav-vertrauen" },
    { to: "/admin/viewer-cta", label: "360°-CTA", icon: MousePointer, testId: "admin-nav-viewer-cta" },
    { to: "/admin/aktion", label: "Referenz-Aktion", icon: Megaphone, testId: "admin-nav-aktion" },
    { to: "/admin/aktions-seite", label: "Aktions-Seite (/aktion)", icon: Sparkles, testId: "admin-nav-aktions-seite" },
    { to: "/admin/sylt", label: "Sylt", icon: Anchor, testId: "admin-nav-sylt" },
    { to: "/admin/qr-kampagne", label: "Erleben-Landing (QR-Kampagne)", icon: QrCode, testId: "admin-nav-qr-kampagne" },
    { to: "/admin/rechtstexte", label: "Rechtstexte", icon: Scale, testId: "admin-nav-rechtstexte" },
];

export default function AdminLayout() {
    const { status, user, logout } = useAuth();
    const location = useLocation();

    if (status === "checking") {
        return (
            <div className="min-h-screen flex items-center justify-center text-hf-muted">
                <span className="hf-eyebrow">Wird geladen…</span>
            </div>
        );
    }
    if (status !== "authed") {
        return <Navigate to="/admin" replace state={{ from: location.pathname }} />;
    }

    return (
        <div
            className="min-h-screen"
            style={{ background: "hsl(var(--hf-surface))" }}
            data-testid="admin-layout"
        >
            {/* Alle Admin-Routen aus dem Suchmaschinen-Index halten. */}
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div className="flex min-h-screen">
                {/* Sidebar */}
                <aside
                    className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r md:sticky md:top-0 md:h-screen md:overflow-y-auto"
                    style={{
                        background: "hsl(var(--hf-ink))",
                        color: "hsl(var(--hf-bg))",
                        borderColor: "hsl(var(--hf-dark-2))",
                    }}
                >
                    <div className="px-6 py-6 border-b" style={{ borderColor: "hsl(var(--hf-dark-2))" }}>
                        <Link to="/admin/dashboard" className="flex items-center gap-3">
                            <span
                                className="inline-flex items-center justify-center w-10 h-10 font-display text-lg"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                }}
                            >
                                H
                            </span>
                            <span className="flex flex-col leading-tight">
                                <span className="font-display text-lg">HypeFly</span>
                                <span
                                    className="hf-eyebrow"
                                    style={{ fontSize: "10px", color: "hsl(var(--hf-gold-soft))" }}
                                >
                                    Admin
                                </span>
                            </span>
                        </Link>
                    </div>
                    <nav className="flex-1 px-3 py-4 space-y-1 text-sm">
                        {NAV.map((n) => {
                            const active = location.pathname.startsWith(n.to);
                            const Icon = n.icon;
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    data-testid={n.testId}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors"
                                    style={{
                                        background: active
                                            ? "hsl(var(--hf-dark-2))"
                                            : "transparent",
                                        color: active
                                            ? "hsl(var(--hf-gold-soft))"
                                            : "hsl(var(--hf-bg) / 0.85)",
                                    }}
                                >
                                    <Icon size={16} />
                                    <span>{n.label}</span>
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="px-3 py-4 border-t space-y-2" style={{ borderColor: "hsl(var(--hf-dark-2))" }}>
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm hover:bg-hf-dark-2"
                            style={{ color: "hsl(var(--hf-bg) / 0.75)" }}
                        >
                            <ExternalLink size={14} />
                            <span>Website ansehen</span>
                        </a>
                        <button
                            type="button"
                            onClick={() => void logout()}
                            data-testid="admin-logout"
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm hover:bg-hf-dark-2"
                            style={{ color: "hsl(var(--hf-bg) / 0.85)" }}
                        >
                            <LogOut size={14} />
                            <span>Abmelden</span>
                        </button>
                    </div>
                </aside>

                {/* Main */}
                <div className="flex-1 min-w-0 flex flex-col">
                    <header
                        className="flex items-center justify-between px-6 md:px-10 py-4 border-b"
                        style={{
                            background: "hsl(var(--hf-bg))",
                            borderColor: "hsl(var(--hf-line))",
                        }}
                    >
                        <div>
                            <div className="hf-eyebrow" style={{ fontSize: "10px" }}>Admin</div>
                            <div className="font-display text-lg" data-testid="admin-header-title">
                                HypeFly · Verwaltung
                            </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <div className="text-right hidden sm:block">
                                <div className="text-xs text-hf-muted">Angemeldet als</div>
                                <div className="text-hf-ink" data-testid="admin-current-user">
                                    {user?.email}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => void logout()}
                                className="hf-btn hf-btn-ghost"
                                data-testid="admin-header-logout"
                            >
                                <LogOut size={14} /> <span>Abmelden</span>
                            </button>
                        </div>
                    </header>
                    <main className="flex-1 p-6 md:p-10">
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
}
